/**
 * Global tuning constants. All gameplay values funnel through here so the
 * Game Economist can balance without hunting through engine code.
 */

export const WORLD = {
  /**
   * Map size — shrunk from 6000 → 5000 to raise action density.
   * With ~28 bots + 2000 pellets the world feels populated rather than empty.
   * Doubling-the-area trade was a poor one at 22 bots.
   */
  SIZE: 5000,
  /** Soft border buffer where input thrust is dampened. */
  EDGE_PADDING: 64,
} as const;

export const CAMERA = {
  /**
   * Follow stiffness — lower = floatier "drone above the snake" feel. We use
   * a critically-damped exponential lerp; reducing stiffness adds visual
   * mass without overshoot. 2.35 closes ~50% in 0.3s — reads as a tethered
   * floating drone that organically *lags* behind the head on direction
   * changes. (Was 2.8, which read as slightly too locked.)
   */
  FOLLOW_STIFFNESS: 2.35,
  /** Zoom smoothing — slower so growth feels cinematic. */
  ZOOM_STIFFNESS: 2.4,
  /** Floor on zoom. */
  MIN_ZOOM: 0.62,
  /**
   * Ceiling on zoom — raised to 2.6 to accommodate the viewport-scale
   * multiplier on large displays (a 4K monitor at base 1.0 zoom would
   * otherwise need values above the previous 1.6 cap).
   */
  MAX_ZOOM: 2.6,
  START_ZOOM: 1.0,
  /** Zoom curve exponent from radius. */
  ZOOM_POWER: 0.28,
  /**
   * Viewport reference dimension (CSS pixels). zoom is scaled by
   * `min(vw, vh) / VIEWPORT_REF` so the snake fills a consistent fraction
   * of screen height on any device. This is the screen-adaptation seam.
   *
   * 720 = a small laptop's vertical resolution. A 1440-tall display gets
   * a 2× zoom multiplier; a 600-tall mobile gets ~0.83×.
   */
  VIEWPORT_REF: 720,
  /** Bounds the viewport multiplier so extreme aspect ratios don't break. */
  VIEWPORT_SCALE_MIN: 0.85,
  VIEWPORT_SCALE_MAX: 2.0,
  /**
   * Seconds-of-velocity to project the camera target ahead of the head.
   * Higher = more anticipation; lower = anchored.
   */
  LOOKAHEAD_TIME: 0.14,
  /**
   * Decay rate of screen-shake amplitude (1/sec). Higher = settles faster.
   * Bumped from 4.5 → 7 so any incidental shake resolves quickly and never
   * accumulates into a rattle.
   */
  SHAKE_DECAY: 7,
  /** Hard cap on shake magnitude in CSS pixels. */
  SHAKE_MAX_PX: 14,
} as const;

export const PLAYER = {
  /** Starting world-space radius for the snake head. */
  START_RADIUS: 18,
  /**
   * Base movement speed in world units per second.
   * Bumped 245 → 285 so the early game reads as quick and lively.
   */
  BASE_SPEED: 285,
  /**
   * Speed = BASE * (START_RADIUS / r) ^ SPEED_FALLOFF.
   * Softened 0.32 → 0.18: a giant snake keeps ~75% of base speed (was ~55%).
   * Combined with gentler zoom (CAMERA.ZOOM_POWER), big snakes finally feel
   * fast on screen instead of double-penalized.
   */
  SPEED_FALLOFF: 0.18,
  /** Minimum speed multiplier (so giants are still controllable). */
  MIN_SPEED_MULT: 0.72,
  /**
   * Steering stiffness — exponential angle-follow rate in `1 - exp(-k*dt)`.
   * Higher = tighter cursor response while still curving smoothly.
   *
   *   Small (r ~ 18)  → TURN_STIFFNESS_MAX  (sharp, precise)
   *   Giant (r >= TURN_STIFFNESS_R_FULL) → TURN_STIFFNESS_MIN (heavy drift)
   */
  TURN_STIFFNESS_MAX: 14.0,
  TURN_STIFFNESS_MIN: 5.0,
  TURN_STIFFNESS_R_FULL: 120,
  /**
   * Steer-target smoothing. Pushed to 32 — at 60Hz this closes ~41% of the
   * gap per frame, so the smoothing is virtually transparent on a
   * still mouse but still kills sub-pixel jitter. Snappier cursor follow.
   */
  STEER_SMOOTHING: 32,
  /**
   * Minimum cursor→head distance (world units) before steering reacts.
   * Tightened: 50→25 base, 1.4→0.7 radius factor — the previous deadzone
   * felt like the snake ignored small corrections on large screens.
   */
  STEER_DEADZONE_FACTOR: 0.7,
  STEER_DEADZONE_MIN: 25,
  /**
   * Speed-ramp stiffness. The snake's actual speed is damped toward its
   * target speed (which depends on size + boost). Higher = snappier
   * acceleration; lower = more "weight" on start/stop.
   */
  ACCEL_STIFFNESS: 10,
} as const;

export const SNAKE = {
  MIN_SEGMENTS: 14,
  MAX_SEGMENTS: 90,
  MASS_FOR_MAX_SEGS: 80_000,
  /** Distance between consecutive segments, scales with radius. */
  SEGMENT_SPACING_FACTOR: 0.78,
  /**
   * Damping stiffness for body chain follow. 14 is the design sweet-spot —
   * tight enough that the body tracks the head, loose enough that curves
   * read as visible arcs (compared to the previous 22 which read as a stick).
   */
  CHAIN_STIFFNESS: 14,
  /** Tail tapers to this fraction of head radius. */
  TAIL_TAPER: 0.45,
  /** Boost mass-drain per second. */
  BOOST_DRAIN: 22,
  /** Boost speed multiplier. Bumped from 1.7 → 1.85 for a stronger punch. */
  BOOST_MULT: 1.85,
  /**
   * Lateral swim wobble amplitude (fraction of head radius). Visual only.
   * Reduced 0.22 → 0.12 — the previous setting visibly shimmied the body
   * even at rest, which the eye reads as "vibration". 0.12 gives a subtle
   * "alive" feel without busy-ness.
   */
  WOBBLE_AMP: 0.12,
  /** Wobble waves per body length. */
  WOBBLE_FREQ: 1.4,
  /** Wobble traveling speed (cycles per second). */
  WOBBLE_SPEED: 1.6,
  /**
   * Color-match head-pulse: brief brightness peak on premium-color eat.
   * Stored as a 0..1 falloff with this exponential rate.
   */
  PULSE_DECAY: 6.5,
} as const;

/**
 * Boost mechanics — what happens when SPACE is held.
 *
 * Mass leaves the snake at BOOST.DRAIN rate (already in SNAKE.BOOST_DRAIN
 * for the historical name). Each chunk of BURN_PER_PELLET mass spawns a
 * trail pellet behind the tail in the snake's team color — recoverable
 * by anyone, creating tactical "burn rubber for food" play.
 *
 * Backward-facing particles also emit at the tail at PARTICLE_RATE/sec
 * for "ignition trail" feel.
 */
export const BOOST_TRAIL = {
  /** Each pellet absorbs this much mass from the burn accumulator. */
  BURN_PER_PELLET: 6,
  /** Backward particle emission rate per second (visual only). */
  PARTICLE_RATE: 18,
  /** Initial backward velocity for trail particles (world u/s). */
  PARTICLE_SPEED: 180,
  /** Wave-speed multiplier applied to the body brightness wave on boost. */
  WAVE_SPEED_MULT: 1.7,
} as const;

/**
 * Death explosion — body segments fly outward briefly before settling.
 * Re-uses Food.dropAt with an initial velocity vector; drag in Food.step
 * decelerates them naturally.
 */
export const DEATH_BURST = {
  /** Min initial outward speed (world u/s). */
  SPEED_MIN: 60,
  /** Max initial outward speed. */
  SPEED_MAX: 160,
} as const;

export const EJECT = {
  /** Mass cost per eject pellet. */
  COST_MASS: 14,
  /** Minimum mass needed to eject. */
  MIN_MASS: 220,
  /** Initial outward speed (world units/sec). */
  SPEED: 420,
  /** Drag rate — the projectile decays to rest. */
  DRAG: 1.8,
  /** Lifespan before respawning as static pellet (sec). */
  LIFE: 3.0,
  /** Grace window before the ejector can re-eat their own pellet. */
  GRACE: 0.5,
  /** Cooldown between ejects (sec). */
  COOLDOWN: 0.12,
} as const;

export const FOOD = {
  /**
   * Density divisor — pellets = floor(WORLD² / DIVISOR).
   * Pushed from 12000 → 9000 per playtest feedback: at 12000 the area
   * around the player would visibly thin out within ~20s of active
   * play (eaten pellets respawn at random world locations, away from
   * the action). With 9000 the floor stays populated. World 5000²
   * yields 2777 → MAX_PELLETS caps at 3000.
   */
  DENSITY_DIVISOR: 9_000,
  /** Hard ceiling regardless of formula. */
  MAX_PELLETS: 3000,
  MIN_RADIUS: 4,
  MAX_RADIUS: 8,
  MASS_PER_PELLET_MIN: 4,
  MASS_PER_PELLET_MAX: 14,
  EAT_RANGE_FACTOR: 1.2,
} as const;

/**
 * Eight-team color palette, evenly spaced 45° apart so every team is
 * mathematically distinguishable on the leaderboard and on the map.
 *
 * Reading order (perceptual):
 *   20°  red-orange
 *   65°  amber-yellow
 *   110° lime-green
 *   155° teal
 *   200° cyan
 *   245° indigo / blue-violet
 *   290° violet
 *   335° magenta-rose
 *
 * With World.spawnBot rotating through this array, the first 8 bots are
 * guaranteed unique colors — so the leaderboard's top 8 reads as 8 distinct
 * identities.
 */
export const TEAM_HUES = [20, 65, 110, 155, 200, 245, 290, 335] as const;
export type TeamHue = (typeof TEAM_HUES)[number];

export const COLOR_MATCH = {
  /** Hue distance (degrees) within which a pellet counts as PERFECT match. */
  PERFECT_DEGREES: 18,
  /** Hue distance for ADJACENT (neighbor) match — full-value fuel. */
  ADJACENT_DEGREES: 55,
  /**
   * Mass multipliers per category. Dropped PERFECT from 3.0 → 2.5 —
   * the 3× bonus dominated scoring and made off-color pellets feel
   * useless; 2.5× still rewards color discipline without trivializing
   * neighbor-color play.
   */
  PERFECT_MULT: 2.5,
  ADJACENT_MULT: 1.0,
  OFF_MULT: 0.55,
  /** Number of particles spawned on a PERFECT eat. Smaller = quieter. */
  BURST_COUNT: 8,
  /** Particle initial speed (world units/sec). */
  BURST_SPEED: 140,
} as const;

export const AI = {
  /**
   * Bots-per-sq-km is ignored when MAX_BOTS is small — kept for documentation.
   * Game design call: 7 fixed-identity rivals (+ player = 8 leaderboard slots)
   * gives each competitor a guaranteed-unique color AND a guaranteed-unique
   * visual design. Anonymous-bot horde was replaced by "boss-class" rivals.
   */
  BOTS_PER_SQ_KM: 1.4,
  MAX_BOTS: 7,
  PERCEPTION_RANGE: 1200,
  THREAT_MASS_MULT: 1.35,
  CHASE_MASS_MULT: 1.6,
  DECISION_INTERVAL: 0.4,
  WANDER_TURN_RATE: 1.8,
  START_MASS_MIN: 240,
  START_MASS_MAX: 1400,
  /**
   * Passive growth applied to every bot per second of life past
   * `PASSIVE_GROWTH_GRACE_S`. The grace window protects fresh respawns
   * from instant inflation; after that, each bot accretes mass at a
   * steady rate that scales their visible size + leaderboard position.
   *
   * Why this exists: without passive growth, bots can only grow through
   * pellet eats and (rarely) killing each other. The player meanwhile
   * eats with 2.5× perfect-color multipliers, combo streaks, AND
   * cashes in bot corpses — leading to "only I am growing". Passive
   * growth lets bots keep proportional pace even when the player
   * snowballs aggressively.
   *
   * Tuning: 2 mass/sec → +120 mass per minute → a 5-min-old bot
   * accreted ~600 extra mass, big enough to feel like a real
   * competitor without breaking the food-chain economy.
   */
  PASSIVE_GROWTH_PER_SEC: 2.0,
  PASSIVE_GROWTH_GRACE_S: 15,
  /** Cap on passive growth so a hour-old bot doesn't become a 50k unkillable.
   *  Past this, bots can still grow by eating but the auto-pump stops. */
  PASSIVE_GROWTH_CEILING: 6000,
  /**
   * Soft inner boundary used by the bot AI when picking a steer target.
   * Anything closer to the world border than this gets pushed inward —
   * stops bots from beelining off-map when a chased prey runs into a
   * wall or a wander target rolls near the edge. Player-aware wander
   * also uses this band: bots try to congregate within `PLAYER_ORBIT`
   * units of the player to keep the action visible to them.
   */
  EDGE_MARGIN: 400,
  /**
   * When a bot has no active chase/flee/food target, this is the radius
   * around the player it picks a wander destination from. Larger than
   * the perception range so the bot doesn't immediately re-engage on
   * arrival — it gives a "drifting toward the action" feel without
   * herding every bot directly onto the player's head.
   */
  PLAYER_ORBIT: 1500,
  /**
   * Probability that an idle bot's wander target biases toward the
   * player's area instead of map-center. Higher = more chaos-around-
   * the-player; lower = more even distribution. 0.7 gives a clearly
   * populated combat zone near the player while still letting some
   * bots roam.
   */
  PLAYER_ORBIT_BIAS: 0.7,
  /**
   * AI bias: prefer their own color when seeking food. Score multiplier
   * applied to matching pellets — makes the world feel team-organized.
   */
  COLOR_PREFERENCE: 2.4,
} as const;

export const DECAY = {
  THRESHOLD: 1500,
  RATE: 0.0035,
} as const;

export const PARTICLES = {
  POOL: 600,
  LIFE: 0.6,
  DRAG: 2.6,
  RADIUS_MIN: 2,
  RADIUS_MAX: 4,
} as const;

/**
 * Powerup economy.
 *
 * Three pickup types occasionally drop into the arena:
 *   - SURGE:  5s of free-cost boost. Common.
 *   - DOUBLE: 8s during which pellets give 2× mass. Moderately common.
 *   - SHIELD: persistent until consumed by one fatal collision. Rare.
 *
 * Spawn rules: jittered interval, capped to MAX_ACTIVE on the map.
 */
export const POWERUP = {
  MAX_ACTIVE: 3,
  /** Average sec between spawn attempts. Jittered ±50% per attempt. */
  SPAWN_INTERVAL_MEAN: 18,
  /** Pickup hitbox radius (world units). Scaled with the visual bump. */
  PICKUP_RANGE: 60,
  /** Visual radius. Tripled per design feedback — pickups now read from
   *  much further away and feel like "premium magical objects". */
  RADIUS: 54,

  // Effect durations (sec)
  SURGE_DURATION: 5.0,
  DOUBLE_DURATION: 8.0,

  // Effects
  SURGE_BOOST_MULT: 2.05,       // stronger than the normal boost (1.85)
  DOUBLE_MASS_MULT: 2.0,

  // Weighted rarity (must sum to 1.0)
  W_SURGE: 0.50,
  W_DOUBLE: 0.35,
  W_SHIELD: 0.15,
} as const;

export const SHAKE = {
  /**
   * Player-death amplitude (CSS pixels). Reduced from 18 → 9 — the slow-mo
   * + descending-sweep audio already sell the moment; a giant rattle is
   * overkill.
   */
  DEATH_PLAYER: 9,
  /**
   * Player-credited kill nearby — small celebratory bump. Was 6, reduced
   * to 2.8 — kills should feel snappy, not whip the screen around.
   * Random bot-vs-bot deaths do NOT trigger any shake at all.
   */
  DEATH_BOT_NEAR: 2.8,
  /** Within this world distance a bot kill registers shake. */
  BOT_NEAR_RANGE: 1000,
} as const;

/**
 * Digesting orbs — the visible "balls in the belly" feature.
 *
 * When a snake eats a pellet that matches its own team color (perfect
 * match), an orb spawns at the head and travels along the body to the
 * tail, where it disappears. Reads as "I just ate this — you can see it
 * passing through me". The faster the orb, the snappier the visual
 * feedback; the more orbs you allow, the busier the body looks.
 */
export const DIGEST = {
  /** How fast an orb travels along the body, in u/s where u ∈ [0, 1]. */
  ORB_SPEED: 0.22,
  /** Cap on simultaneously visible orbs per snake. Oldest is dropped on overflow. */
  MAX_ORBS: 10,
  /** Orb visual radius as a fraction of body width at the orb's position. */
  ORB_RADIUS_FACTOR: 0.62,
} as const;

export const GRID = {
  MINOR_STEP: 100,
  MAJOR_EVERY: 5,
  MINOR_COLOR: 'rgba(120, 180, 255, 0.05)',
  MAJOR_COLOR: 'rgba(120, 180, 255, 0.12)',
  BORDER_COLOR: 'rgba(34, 230, 255, 0.55)',
  BG_INNER: '#0a0d1e',
  BG_OUTER: '#03040a',
} as const;

export const LOOP = {
  /**
   * Fixed physics step (seconds). 1/60 is the slither-class sweet spot —
   * smooth motion on 60Hz monitors, interleaves cleanly on 120/144Hz.
   * Physics is decoupled from rAF so frame-rate jitter no longer enters
   * the simulation.
   */
  FIXED_DT: 1 / 60,
  /**
   * Hard ceiling on real elapsed time fed into the accumulator per frame.
   * After a tab-blur the elapsed could be many seconds — we discard the
   * excess rather than spending the next half-second catching up.
   */
  MAX_FRAME_DT: 0.1,
  /**
   * Catch-up cap: never run more than this many physics steps per
   * rendered frame. Prevents the "spiral of death" if a single step
   * accidentally outgrows the fixed-dt budget.
   */
  MAX_STEPS_PER_FRAME: 5,
} as const;

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

export const massToRadius = (mass: number): number => Math.sqrt(Math.max(1, mass) / Math.PI);
export const radiusToMass = (r: number): number => Math.PI * r * r;

/** Shortest angular distance between two hues in degrees, in [0, 180]. */
export const hueDistance = (a: number, b: number): number => {
  const d = Math.abs(((a - b) % 360 + 360) % 360);
  return d > 180 ? 360 - d : d;
};

/**
 * Classify a pellet's hue match against a snake's identity hue and return
 * the mass multiplier. Single seam — the Economist tunes here.
 */
export const matchMultiplier = (snakeHue: number, pelletHue: number): number => {
  const d = hueDistance(snakeHue, pelletHue);
  if (d <= COLOR_MATCH.PERFECT_DEGREES) return COLOR_MATCH.PERFECT_MULT;
  if (d <= COLOR_MATCH.ADJACENT_DEGREES) return COLOR_MATCH.ADJACENT_MULT;
  return COLOR_MATCH.OFF_MULT;
};
